import { useState } from 'react';
import { API_URL, API_VERSION } from '@constants';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';

const ENDPOINT_URL = '/auth/check-verification-token';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type CheckVerificationTokenInput = paths[url]['post']['requestBody']['content']['application/json'];
export interface CheckVerificationTokenSuccessResponse {
    data: paths[url]['post']['responses']['200']['content']['application/json'];
}

/**
 * Requests endpoint to create a service.
 */
export const useCheckVerificationToken = () => {
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();

    const [loading, setLoading] = useState<boolean>(false);
    const [success, setSuccess] = useState<string | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);

    // general error message (for general errors, example: 500)
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const request = async (props: CheckVerificationTokenInput) => {
        setLoading(true);
        setSuccess(null);
        setErrorMessage(null);

        try {
            const response = await fetch(`${API_URL}${ENDPOINT_URL}`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ ...getCommonParameters(), ...props }),
            });

            if (!response.ok) {
                const error = new Error() as ApiError;

                try {
                    const json = await response.json();
                    error.message = json.message;
                    error.errors = json.errors;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } catch (error: any) {
                    error.message = '';
                }

                error.statusCode = response.status;

                throw error;
            }

            const data = await response.json();

            setSuccess(data.message);

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            handleCommonErrors(error);
            error.message && setErrorMessage(error.message);
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return { request, loading, success, errorMessage, lastResponseTime };
};
